import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../App';
import { ApiError, api, useQuery } from '../api/client';
import { Select } from '../components/Select';
import { Card, ErrorNote } from '../components/ui';

const TRISTATE = [
  { value: '', label: 'Not recorded' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** A sensible subset; the browser supplies the full list via Intl where it can. */
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export function NewCallPage() {
  const navigate = useNavigate();
  const { capabilities } = useSession();
  const { data: sdrList } = useQuery(() => api.sdrs(), []);
  const { data: rubric } = useQuery(() => api.rubric(), []);

  const [file, setFile] = useState<File | null>(null);
  // Controlled because Select is not a native form control; each one renders a
  // hidden input under `name`, so FormData still picks the values up.
  const [sdrId, setSdrId] = useState('');
  const [prospectType, setProspectType] = useState('unknown');
  const [prospectTimezone, setProspectTimezone] = useState('');
  const [loggedStatus, setLoggedStatus] = useState('');
  const [meetingBooked, setMeetingBooked] = useState('');
  const [meetingHeld, setMeetingHeld] = useState('');
  const [inviteClient, setInviteClient] = useState('');
  const [inviteAe, setInviteAe] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAudio = file ? /\.(mp3|wav|m4a|mp4|ogg|webm|flac|aac)$/i.test(file.name) : false;
  const audioBlocked = isAudio && capabilities && !capabilities.audioTranscription;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!sdrId) {
      setError(new ApiError(400, 'Choose which SDR made this call.'));
      return;
    }
    if (!file && !transcriptText.trim()) {
      setError(new ApiError(400, 'Supply a transcript: paste it, upload a transcript file, or upload audio.'));
      return;
    }
    if (audioBlocked) {
      setError(
        new ApiError(
          400,
          'Audio transcription is not configured on this server, so an audio-only submission cannot be audited. Paste or upload the transcript instead.',
        ),
      );
      return;
    }

    setBusy(true);
    try {
      const form = new FormData(formRef.current!);
      if (file) form.set('file', file);
      else form.delete('file');
      const { callId } = await api.createCall(form);
      navigate(`/calls/${callId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'The call could not be submitted.'));
    } finally {
      setBusy(false);
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>Submit a call</h1>
          <p className="page-sub">Every field is optional except the SDR and the call content. Missing data is reported as unavailable, never guessed.</p>
        </div>
      </header>

      <div className="page-body page-enter">
        <form ref={formRef} onSubmit={submit}>
          <div className="stack" style={{ maxWidth: 860 }}>
            <ErrorNote error={error} />
            {error?.issues?.length ? (
              <div className="alert">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {error.issues.map((i) => (
                    <li key={i.field}>
                      <strong>{i.field}</strong>: {i.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <Card title="The call">
              <div className="stack">
                <div
                  className={`dropzone ${dragOver ? 'is-over' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <div className="dropzone-title">{file ? 'Replace the file' : 'Drop a recording or transcript here'}</div>
                  <div className="dropzone-sub">
                    Audio (mp3, wav, m4a, mp4, ogg, webm, flac, aac) or a transcript (txt, vtt, srt, json, md)
                    {capabilities && ` · up to ${capabilities.maxUploadMb}MB`}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  name="file"
                  className="sr-only"
                  accept=".mp3,.wav,.m4a,.mp4,.ogg,.webm,.flac,.aac,.txt,.vtt,.srt,.json,.md"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />

                {file && (
                  <div className="row">
                    <span className="file-chip">
                      {file.name}
                      <span className="dim">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        Remove
                      </button>
                    </span>
                  </div>
                )}

                {audioBlocked && (
                  <div className="alert alert-warn">
                    This server has no transcription provider configured, so an audio file cannot be turned into a transcript here.
                    Paste the transcript below instead, or set <span className="mono">ASSEMBLYAI_API_KEY</span> on the server.
                  </div>
                )}

                <div className="field">
                  <label htmlFor="transcriptText">Or paste the transcript</label>
                  <textarea
                    id="transcriptText"
                    name="transcriptText"
                    rows={8}
                    value={transcriptText}
                    onChange={(e) => setTranscriptText(e.target.value)}
                    placeholder={'[00:02] [SDR] Hi, is this Dana? …\n[00:11] [PROSPECT] Sure, go ahead.'}
                  />
                  <span className="field-hint">
                    Speaker labels make the audit far stronger. <span className="mono">[SDR]</span> /{' '}
                    <span className="mono">SDR:</span> / <span className="mono">Speaker A:</span> are all understood. Without
                    them, findings that depend on who said what are reported as Evidence unavailable rather than scored.
                  </span>
                </div>
              </div>
            </Card>

            <Card title="Who and when">
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="sdrId">SDR *</label>
                  <Select
                    id="sdrId"
                    name="sdrId"
                    block
                    placeholder="Select the SDR"
                    value={sdrId}
                    onChange={setSdrId}
                    options={(sdrList?.sdrs ?? []).map((s) => ({ value: s.id, label: s.name }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="calledAt">Call date &amp; time</label>
                  <input id="calledAt" name="calledAt" type="datetime-local" defaultValue={new Date().toISOString().slice(0, 16)} />
                </div>
                <div className="field">
                  <label htmlFor="durationSec">Duration (seconds)</label>
                  <input id="durationSec" name="durationSec" type="number" min={1} placeholder="e.g. 420" />
                </div>
                <div className="field">
                  <label htmlFor="campaign">Campaign</label>
                  <input id="campaign" name="campaign" type="text" placeholder="e.g. OH-Q3" />
                </div>
              </div>
            </Card>

            <Card title="Prospect">
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="prospectName">Name</label>
                  <input id="prospectName" name="prospectName" type="text" />
                </div>
                <div className="field">
                  <label htmlFor="prospectCompany">Company</label>
                  <input id="prospectCompany" name="prospectCompany" type="text" />
                </div>
                <div className="field">
                  <label htmlFor="prospectType">Type</label>
                  <Select
                    id="prospectType"
                    name="prospectType"
                    block
                    value={prospectType}
                    onChange={setProspectType}
                    options={(rubric?.prospectTypes ?? ['unknown']).map((t) => ({ value: t, label: t }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="prospectMarket">Market</label>
                  <input id="prospectMarket" name="prospectMarket" type="text" placeholder="e.g. Columbus, OH" />
                </div>
                <div className="field">
                  <label htmlFor="prospectTimezone">Timezone</label>
                  <Select
                    id="prospectTimezone"
                    name="prospectTimezone"
                    block
                    value={prospectTimezone}
                    onChange={setProspectTimezone}
                    options={[
                      { value: '', label: 'Not known' },
                      ...TIMEZONES.map((t) => ({ value: t, label: t.replace('America/', '').replace(/_/g, ' ') })),
                    ]}
                  />
                  <span className="field-hint">
                    Required to check the 8am–9pm calling-hours rule. Without it the rule is reported as unchecked, not as a pass.
                  </span>
                </div>
                <div className="field">
                  <label htmlFor="prospectPhone">Phone</label>
                  <input id="prospectPhone" name="prospectPhone" type="text" />
                  <span className="field-hint">Used to spot repeat bookings of the same prospect.</span>
                </div>
              </div>
            </Card>

            <Card title="What the SDR logged" note="Audited against the call, never trusted">
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="sdrLoggedStatus">Disposition / status</label>
                  <Select
                    id="sdrLoggedStatus"
                    name="sdrLoggedStatus"
                    block
                    value={loggedStatus}
                    onChange={setLoggedStatus}
                    options={[
                      { value: '', label: 'Not logged' },
                      ...(rubric?.sdrLoggedStatuses ?? []).map((s) => ({ value: s, label: s })),
                    ]}
                  />
                </div>
                <div className="field">
                  <label htmlFor="meetingBooked">Meeting booked</label>
                  <Select
                    id="meetingBooked"
                    name="meetingBooked"
                    block
                    value={meetingBooked}
                    onChange={setMeetingBooked}
                    options={TRISTATE.map((o) => ({ value: o.value, label: o.label }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="meetingHeld">Meeting held</label>
                  <Select
                    id="meetingHeld"
                    name="meetingHeld"
                    block
                    value={meetingHeld}
                    onChange={setMeetingHeld}
                    options={TRISTATE.map((o) => ({ value: o.value, label: o.label }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="meetingAt">Meeting date &amp; time</label>
                  <input id="meetingAt" name="meetingAt" type="datetime-local" />
                  <span className="field-hint">Lets a no-show be told apart from a booking that simply has not happened yet.</span>
                </div>
                <div className="field">
                  <label htmlFor="calendarInviteClient">Calendar invite to client</label>
                  <Select
                    id="calendarInviteClient"
                    name="calendarInviteClient"
                    block
                    value={inviteClient}
                    onChange={setInviteClient}
                    options={TRISTATE.map((o) => ({ value: o.value, label: o.label }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="calendarInviteAe">Calendar invite to AE</label>
                  <Select
                    id="calendarInviteAe"
                    name="calendarInviteAe"
                    block
                    value={inviteAe}
                    onChange={setInviteAe}
                    options={TRISTATE.map((o) => ({ value: o.value, label: o.label }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="emailCaptured">Email captured</label>
                  <input id="emailCaptured" name="emailCaptured" type="text" />
                </div>
                <div className="field">
                  <label htmlFor="crmPipeline">GHL pipeline</label>
                  <input id="crmPipeline" name="crmPipeline" type="text" />
                </div>
                <div className="field">
                  <label htmlFor="crmStage">GHL stage</label>
                  <input id="crmStage" name="crmStage" type="text" />
                </div>
              </div>

              <div className="field mt-3">
                <label htmlFor="crmNotes">CRM notes</label>
                <textarea id="crmNotes" name="crmNotes" rows={4} placeholder="Paste the notes the SDR logged for this call." />
                <span className="field-hint">
                  Compared against the transcript. Notes that contradict the call are an integrity flag; notes that are simply
                  thin are a hygiene score, not a contradiction.
                </span>
              </div>
            </Card>

            <div className="row" style={{ justifyContent: 'flex-end', paddingBottom: 24 }}>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Submitting…' : 'Submit and run the audit'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
